import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Section from 'components/common/Section/Section'
import { colors } from 'styles/colors'
import { Box, Grid } from '@mui/material'
import { useProductContext } from 'context/ProductContext'
import PencilIcon from 'icons/PencilIcon'
import SaveIcon from 'icons/SaveIcon'
import XIcon from 'icons/XIcon'
import { useUploadProductImages } from 'hooks/useProduct'
import { snackbarService } from 'services/snackbarService'
import { IPhotoSize } from 'types/types'
import ProductImage from '../ProductImage/ProductImage'
import AddImageIcon from 'icons/AddImageIcon'

interface Props {
  shared: any;
}

function ProductImagesDetails({ shared }: Props) {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { product, refetch: refetchProduct } = useProductContext();
  const [images, setImages] = useState<IPhotoSize[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const {
    setCarouselImages,
    setCarouselIndex,
    setCarouselOpen,
  } = shared;

  useEffect(() => {
    setImages(product.mediaFiles);
  }, []);

  const { mutate: updateMutation, isLoading } = useUploadProductImages(
    (data: any) => {
      setIsEditing(false);
      snackbarService.showSnackbar('Product images has been updated successfully', 'success');
      refetchProduct();
    },
    (err: any) => { },
  )

  const newImages = useMemo(() => {
    return images.filter(img => !img.id);
  }, [images]);

  const handleSave = () => {
    if(newImages.length > 0){
      updateMutation({
        productId: product.id,
        files: newImages,
      });
    } else {
      setIsEditing(false);
      snackbarService.showSnackbar('Product images have been saved successfully', 'success');
    }
  }

  const handleRemove = useCallback(
    (index: number) => {
      setImages((prev: IPhotoSize[]) =>
        prev.filter((_, i) => i !== index)
      );
    },
    [setImages]
  );

  // Only the photos picked but not yet uploaded are dropped: the ones already on
  // the product are gone the moment their delete is confirmed, which is a
  // separate act with its own dialog.
  const handleCancel = () => {
    setImages(product.mediaFiles ?? []);
    setIsEditing(false);
  }

  const handleAddPhotoClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const imageUrl = URL.createObjectURL(file);

      const newImage: IPhotoSize = {
        small: imageUrl,
        medium: imageUrl,
        large: imageUrl,
      };

      setImages((prev: IPhotoSize[]) => [...prev, newImage]);

      e.target.value = "";
    },
    [images.length, setImages]
  );

  return (
    <Box mt={3}>
      <Section
        title="Product Images"
        buttons={[
          ...(isEditing
            ? [
              {
                icon: <AddImageIcon size={20} color={colors.primary} />,
                title: 'Add',
                onClick: () => handleAddPhotoClick(),
                disabled: isLoading,
              },
              {
                icon: <SaveIcon size={20} color={colors.success} />,
                title: 'Save',
                onClick: () => {
                  handleSave();
                },
                loading: isLoading,
              },
              {
                icon: <XIcon size={15} color={colors.error} />,
                title: 'Cancel',
                onClick: () => handleCancel(),
                disabled: isLoading,
              },
            ]
            : [
              {
                icon: <PencilIcon size={20} color={colors.primary} />,
                title: 'Edit',
                onClick: () => setIsEditing((prev) => !prev),
                disabled: isLoading,
              }
            ]),
        ]}
        content={
          <Grid container spacing={2}>
            {images.map((image, index) => (
              <Grid
                key={`${image?.id ?? "local"}-${index}`}
              >
                <ProductImage
                  index={index}
                  item={image}
                  handleRemove={handleRemove}
                  isEditMode={isEditing}
                  onPreview={() => {
                    setCarouselImages(images);
                    setCarouselIndex(index);
                    setCarouselOpen(true);
                  }}
                />
              </Grid>
            ))}
          </Grid>

        }
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleFileSelected}
      />
    </Box>
  )
}

export default memo(ProductImagesDetails)